import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import * as indexedDBService from '../utils/indexedDBService';

interface SyncStatus {
  isLoading: boolean;
  error: string | null;
  lastSynced: Date | null;
}

/**
 * Hook to initialize local user data when a user logs in
 */
export const useSyncUserData = (): SyncStatus => {
  const { currentUser, userProfile } = useAuth();
  const [status, setStatus] = useState<SyncStatus>({
    isLoading: false,
    error: null,
    lastSynced: null
  });

  // Init local data when user logs in
  useEffect(() => {
    const initLocalData = async () => {
      if (!currentUser) return;

      try {
        setStatus(prev => ({ ...prev, isLoading: true, error: null }));

        // Initialize IndexedDB
        await indexedDBService.initDB();

        // Set current user for IndexedDB operations
        indexedDBService.setCurrentUser(currentUser.uid);

        // Store user profile data if available
        if (userProfile) {
          await indexedDBService.putItem('user_profiles', {
            ...userProfile,
            id: currentUser.uid
          });
        }

        setStatus({
          isLoading: false,
          error: null,
          lastSynced: new Date()
        });

      } catch (error) {
        console.error('Error initializing user data:', error);
        setStatus({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Unknown error initializing user data',
          lastSynced: null
        });
      }
    };

    initLocalData();
  }, [currentUser, userProfile]);

  return status;
};

export default useSyncUserData; 
